<template>
  <div id="app">
    <router-view v-if="!hasError" />
    <div v-else class="error-boundary">
      <h3>应用程序遇到错误</h3>
      <p>{{ errorMessage }}</p>
      <button @click="reloadApp" class="reload-btn">重新加载</button>
    </div>
  </div>
</template>

<script>
import { ref, onErrorCaptured, onMounted } from 'vue'
import { initWechatEnvironment } from '@/utils/wechatEnvironment'

export default {
  name: 'AppMain',
  setup() {
    const hasError = ref(false)
    const errorMessage = ref('')
    const setupKeyboardScrollFix = () => {
      const ua = navigator.userAgent.toLowerCase()
      const isIOS = /iphone|ipad|ipod/.test(ua)
      const isWechat = ua.includes('micromessenger')
      let paddingApplied = false
      const applyPadding = (px) => {
        const body = document.body
        if (px && px > 0) {
          body.style.paddingBottom = `${px}px`
        } else {
          body.style.paddingBottom = ''
        }
      }
      const updateViewport = () => {
        if (window.visualViewport) {
          const vv = window.visualViewport
          const vh = vv.height * 0.01
          document.documentElement.style.setProperty('--vh', `${vh}px`)
          if (paddingApplied) {
            const kb = Math.max(0, window.innerHeight - vv.height)
            applyPadding(kb)
          }
        }
      }
      if (isIOS && isWechat) {
        document.addEventListener('focusin', (e) => {
          const t = e.target
          const tag = t && t.tagName ? t.tagName.toLowerCase() : ''
          if (tag === 'input' || tag === 'textarea') {
            paddingApplied = true
            updateViewport()
            setTimeout(() => {
              if (t && typeof t.scrollIntoView === 'function') {
                t.scrollIntoView({ block: 'center', inline: 'nearest' })
              }
            }, 50)
          }
        })
        document.addEventListener('focusout', (e) => {
          const t = e.target
          const tag = t && t.tagName ? t.tagName.toLowerCase() : ''
          if (tag === 'input' || tag === 'textarea') {
            paddingApplied = false
            applyPadding(0)
          }
        })
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', updateViewport)
          window.visualViewport.addEventListener('scroll', updateViewport)
          updateViewport()
        }
      }
    }
    
    // Error boundary
    onErrorCaptured((error, instance, info) => {
      console.error('App Error Captured:', error, info)
      hasError.value = true
      errorMessage.value = error.message || '未知错误'
      return false // Prevent error from propagating
    })
    
    const reloadApp = () => {
      hasError.value = false
      errorMessage.value = ''
      window.location.reload()
    }
    
    onMounted(async () => {
      try {
        // 使用统一的微信环境初始化
        const envInfo = initWechatEnvironment()
        console.log('微信环境信息:', envInfo)
        setupKeyboardScrollFix()
        
        // 注释掉重复的分享初始化，统一由main.js处理
        // await initializeGlobalShare()
        
        // 初始化百度统计
        var _hmt = _hmt || [];
        (function() {
          var hm = document.createElement("script");
          hm.src = "https://hm.baidu.com/hm.js?8dce56719d57541e6e40cc3382ce1fb0";
          var s = document.getElementsByTagName("script")[0]; 
          s.parentNode.insertBefore(hm, s);
        })();
      } catch (error) {
        console.error('WeChat initialization error:', error)
      }
    })
  
    
    return {
      hasError,
      errorMessage,
      reloadApp
    }
  }
}
</script>


<style>
html, body {
  margin: 0;
  padding: 0;
  background-color: #f7f8fa;
  overflow-x: hidden;
  height: 100%;
}

body {
  overflow-y: auto;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica,
    Segoe UI, Arial, Roboto, 'PingFang SC', 'miui', 'Hiragino Sans GB', 'Microsoft Yahei',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  min-height: 100%;
  overflow: visible;
}

/* 微信环境下隐藏H5页面底部前进后退横栏 */
@media screen and (max-width: 768px) {
  html {
    /* 禁用长按菜单 */
    -webkit-touch-callout: none;
    /* 禁用文本选择 */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    /* 禁用双击缩放 */
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }
  
  body {
    /* 允许正常滚动 */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
  }
  
  #app {
    /* 允许正常滚动 */
    width: 100%;
    min-height: 100vh;
  }
}

/* 针对微信浏览器的特殊处理 */
@media screen and (max-width: 768px) {
  /* 检测微信环境并应用样式 */
  html[data-wechat="true"] {
    /* 强制隐藏浏览器UI */
    -webkit-appearance: none;
    /* 禁用系统默认的触摸行为 */
    touch-action: manipulation;
    /* 使用CSS自定义属性设置高度 */
    height: calc(var(--vh, 1vh) * 100);
  }
  
  html[data-wechat="true"] body {
    /* 微信环境下允许正常滚动 */
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  html[data-wechat="true"] #app {
    /* 微信环境下允许正常滚动 */
    overflow: visible;
  }
}

/* 微信环境下的全屏样式 */
html[data-wechat="true"] {
  /* 隐藏滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

html[data-wechat="true"]::-webkit-scrollbar {
  display: none;
}

/* 全局van-button样式统一 */
.van-button {
  border-radius: 25px !important;
}

.van-button--primary {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--success {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--warning {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--danger {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--default {
  background: white !important;
  border-color: #ddd !important;
  color: black !important;
}

.van-button--plain {
  background: white !important;
  border-color: #ddd !important;
  color: black !important;
}

/* 全局van-dialog footer 样式 */
:deep(.van-dialog__footer) {
  padding: 8px !important;
  gap: 8px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* 更强的选择器确保样式生效 */
.van-dialog .van-dialog__footer {
  padding: 12px !important;
  gap: 12px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* 全局样式，不使用scoped */
.van-dialog__footer {
  padding: 12px !important;
  gap: 12px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.error-boundary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: #f5f5f5;
}

.error-boundary h3 {
  color: #e74c3c;
  margin-bottom: 16px;
  font-size: 20px;
}

.error-boundary p {
  color: #666;
  margin-bottom: 24px;
  max-width: 400px;
  line-height: 1.5;
}

.reload-btn {
  background-color: #007aff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.reload-btn:hover {
  background-color: #0056b3;
}

.reload-btn:active {
  background-color: #004494;
}
</style>
